import React, { useRef, useCallback } from 'react';
import Editor from '@monaco-editor/react';

/**
 * CodeEditor component using Monaco Editor
 * Supports HTML, CSS, and JavaScript syntax highlighting
 */
const CodeEditor = ({ 
  value, 
  onChange, 
  language = 'html',
  readOnly = false,
  height = '100%',
  theme = 'vs-dark'
}) => {
  const editorRef = useRef(null);

  const handleEditorDidMount = useCallback((editor, monaco) => {
    editorRef.current = editor;
    
    // Configure editor options
    editor.updateOptions({
      minimap: { enabled: true },
      fontSize: 14,
      fontFamily: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', Consolas, monospace",
      lineNumbers: 'on',
      wordWrap: 'on',
      automaticLayout: true,
      scrollBeyondLastLine: false,
      formatOnPaste: true,
      formatOnType: true,
      tabSize: 2,
      insertSpaces: true,
      renderWhitespace: 'selection',
      bracketPairColorization: { enabled: true },
      guides: {
        bracketPairs: true,
        indentation: true
      }
    });

    // Add custom HTML autocomplete suggestions
    if (language === 'html') {
      monaco.languages.registerCompletionItemProvider('html', {
        provideCompletionItems: () => ({
          suggestions: [
            {
              label: 'div',
              kind: monaco.languages.CompletionItemKind.Snippet,
              insertText: '<div class="$1">\n\t$2\n</div>',
              insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              documentation: 'Insert a div element'
            },
            {
              label: 'section',
              kind: monaco.languages.CompletionItemKind.Snippet,
              insertText: '<section class="$1">\n\t$2\n</section>',
              insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
              documentation: 'Insert a section element'
            }
          ]
        })
      });
    }

    // Add keyboard shortcut for formatting
    editor.addCommand(monaco.KeyMod.Alt | monaco.KeyMod.Shift | monaco.KeyCode.KeyF, () => {
      editor.getAction('editor.action.formatDocument').run();
    });
  }, [language]);

  const handleEditorChange = useCallback((newValue) => {
    if (onChange) {
      onChange(newValue);
    }
  }, [onChange]);

  // Map file types to Monaco language identifiers
  const getMonacoLanguage = (lang) => {
    const languageMap = {
      'html': 'html',
      'css': 'css',
      'javascript': 'javascript',
      'js': 'javascript',
      'json': 'json',
      'xml': 'xml'
    };
    return languageMap[lang] || 'html';
  };

  return (
    <div className="h-full w-full rounded-lg overflow-hidden border border-gray-700">
      <Editor
        height={height}
        language={getMonacoLanguage(language)}
        value={value}
        theme={theme}
        onChange={handleEditorChange}
        onMount={handleEditorDidMount}
        options={{
          readOnly,
          minimap: { enabled: true },
          fontSize: 14,
          lineNumbers: 'on',
          wordWrap: 'on',
          automaticLayout: true,
          scrollBeyondLastLine: false,
          formatOnPaste: true,
          tabSize: 2,
          insertSpaces: true
        }}
        loading={
          <div className="flex items-center justify-center h-full bg-gray-900">
            <div className="text-gray-400">Loading editor...</div>
          </div>
        }
      />
    </div>
  );
};

export default CodeEditor;

